import { useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useRegister } from '@/features/auth/hooks'
import { ApiError } from '@/lib/apiClient'
import { Button } from '@/components/ui/Button'
import { Notice } from '@/components/ui/Feedback'
import { PasswordField } from '@/components/ui/PasswordField'

export default function RegisterPage() {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const register = useRegister()
  const navigate = useNavigate()
  const location = useLocation()
  const requestedRedirect = new URLSearchParams(location.search).get('redirect')
  const redirectTo = requestedRedirect?.startsWith('/') && !requestedRedirect.startsWith('//') ? requestedRedirect : '/perfil'

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setSubmitted(true)
    if (!fullName.trim() || !email.trim() || password.length < 10 || password !== passwordConfirmation) {
      requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus())
      return
    }
    register.mutate({ fullName: fullName.trim(), email: email.trim(), password }, { onSuccess: () => navigate(redirectTo, { replace: true }) })
  }

  return (
    <div className="auth-page">
      <header className="flow-heading">
        <p className="eyebrow">Cuenta BsPOS</p>
        <h1>Crea tu cuenta</h1>
        <p>Guarda direcciones y consulta el estado de cada pedido. Te enviaremos un enlace para verificar tu correo.</p>
      </header>

      <form ref={formRef} noValidate onSubmit={handleSubmit} className="form-panel">
        <div className="form-field">
          <label htmlFor="register-name">Nombre completo</label>
          <input
            id="register-name"
            type="text"
            autoComplete="name"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            aria-invalid={submitted && !fullName.trim()}
            aria-describedby={submitted && !fullName.trim() ? 'register-name-error' : undefined}
          />
          {submitted && !fullName.trim() && <p id="register-name-error" className="field-error" role="alert">Escribe tu nombre completo.</p>}
        </div>

        <div className="form-field">
          <label htmlFor="register-email">Correo electrónico</label>
          <input
            id="register-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={submitted && !email.trim()}
            aria-describedby={submitted && !email.trim() ? 'register-email-error' : undefined}
          />
          {submitted && !email.trim() && <p id="register-email-error" className="field-error" role="alert">Escribe tu correo electrónico.</p>}
        </div>

        <PasswordField label="Contraseña" autoComplete="new-password" value={password} onChange={setPassword}
          hint="Usa al menos 10 caracteres." error={submitted && password.length < 10 ? 'La contraseña debe tener al menos 10 caracteres.' : undefined} />
        <PasswordField label="Confirma tu contraseña" autoComplete="new-password" value={passwordConfirmation} onChange={setPasswordConfirmation}
          error={submitted && password !== passwordConfirmation ? 'Las contraseñas no coinciden.' : undefined} />

        {register.isError && (
          <Notice>
            {register.error instanceof ApiError ? register.error.message : 'No pudimos crear la cuenta.'}
          </Notice>
        )}

        <p className="field-hint">Consulta cómo tratamos tus datos en nuestro <Link to="/aviso-de-privacidad" className="font-semibold text-[color:var(--ui-primary)] underline underline-offset-2">Aviso de privacidad</Link> y las condiciones del servicio en nuestros <Link to="/terminos-y-condiciones" className="font-semibold text-[color:var(--ui-primary)] underline underline-offset-2">Términos y condiciones</Link>.</p>
        <Button type="submit" busy={register.isPending}>Crear cuenta</Button>
      </form>

      <p className="flow-footnote">
        ¿Ya tienes cuenta? <Link to={`/login?redirect=${encodeURIComponent(redirectTo)}`}>Ingresa</Link>.
      </p>
    </div>
  )
}

